'use client';

/**
 * Milestones Dashboard Component
 *
 * Displays milestone-based incentive tracking for a service referral:
 * - Visual milestone timeline
 * - Achievement status tracking
 * - Incentive amounts earned
 * - Overdue milestone alerts
 *
 * Used in case workspace and provider dashboards.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Award,
  Clock,
  CheckCircle,
  Circle,
  AlertTriangle,
  DollarSign,
  ChevronRight,
  Loader2,
  Trophy,
  X,
  Calendar,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  serviceReferralsAPI,
  MilestoneAchievement,
  MilestoneStatus,
  MilestoneType,
} from '@/services/api/serviceReferrals';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface MilestonesDashboardProps {
  referralId: number;
  onMilestoneAchieved?: (milestone: MilestoneAchievement) => void;
  readOnly?: boolean;
  compact?: boolean; // Compact view for sidebar
  className?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const getMilestoneStatusConfig = (t: (key: string, fallback: string) => string): Record<MilestoneStatus, { label: string; color: string; icon: React.ReactNode }> => ({
  pending: { label: t('serviceReferrals.milestones.status.pending', 'Pending'), color: 'gray', icon: <Circle className="w-4 h-4" /> },
  in_progress: { label: t('serviceReferrals.milestones.status.inProgress', 'In Progress'), color: 'blue', icon: <Clock className="w-4 h-4" /> },
  achieved: { label: t('serviceReferrals.milestones.status.achieved', 'Achieved'), color: 'green', icon: <CheckCircle className="w-4 h-4" /> },
  skipped: { label: t('serviceReferrals.milestones.status.skipped', 'Skipped'), color: 'amber', icon: <ChevronRight className="w-4 h-4" /> },
  failed: { label: t('serviceReferrals.milestones.status.failed', 'Failed'), color: 'red', icon: <X className="w-4 h-4" /> },
});

const getMilestoneTypeLabels = (t: (key: string, fallback: string) => string): Record<MilestoneType, string> => ({
  completion: t('serviceReferrals.milestones.type.completion', 'Completion'),
  time_based: t('serviceReferrals.milestones.type.timeBased', 'Time-Based'),
  outcome: t('serviceReferrals.milestones.type.outcome', 'Outcome'),
  engagement: t('serviceReferrals.milestones.type.engagement', 'Engagement'),
});

// ============================================================================
// COMPONENT
// ============================================================================

export function MilestonesDashboard({
  referralId,
  onMilestoneAchieved,
  readOnly = false,
  compact = false,
  className = '',
}: MilestonesDashboardProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();

  // State
  const [milestones, setMilestones] = useState<MilestoneAchievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [achieving, setAchieving] = useState<number | null>(null);
  const [showAchieveModal, setShowAchieveModal] = useState<number | null>(null);
  const [achievementNotes, setAchievementNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Load milestones
  useEffect(() => {
    loadMilestones();
  }, [referralId]);

  const loadMilestones = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await serviceReferralsAPI.getMilestones(referralId);
      // Sort by sequence number
      setMilestones(data.sort((a, b) => a.sequence_number - b.sequence_number));
    } catch (err) {
      console.error('Failed to load milestones:', err);
      setError(t('serviceReferrals.milestones.errors.loadFailed', 'Failed to load milestones'));
    } finally {
      setLoading(false);
    }
  };

  const handleAchieveMilestone = async (milestoneDefId: number) => {
    setAchieving(milestoneDefId);
    setError(null);
    try {
      const achieved = await serviceReferralsAPI.achieveMilestone(referralId, milestoneDefId, {
        achievement_notes: achievementNotes,
      });
      setMilestones(milestones.map((m) =>
        m.milestone_definition_id === milestoneDefId ? achieved : m
      ));
      setShowAchieveModal(null);
      setAchievementNotes('');
      onMilestoneAchieved?.(achieved);
    } catch (err) {
      console.error('Failed to achieve milestone:', err);
      setError(t('serviceReferrals.milestones.errors.achieveFailed', 'Failed to mark milestone as achieved'));
    } finally {
      setAchieving(null);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return fmt.medium(dateStr);
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate summary stats
  const achievedCount = milestones.filter((m) => m.status === 'achieved').length;
  const requiredCount = milestones.filter((m) => m.is_required).length;
  const requiredAchieved = milestones.filter((m) => m.is_required && m.status === 'achieved').length;
  const totalIncentive = milestones.reduce((sum, m) => sum + (m.incentive_amount || 0), 0);
  const earnedIncentive = milestones.reduce((sum, m) => sum + (m.incentive_amount_earned || 0), 0);
  const overdueCount = milestones.filter((m) => m.is_overdue && m.status !== 'achieved').length;
  const progressPct = milestones.length > 0 ? Math.round((achievedCount / milestones.length) * 100) : 0;

  // ========================================================================
  // RENDER HELPERS
  // ========================================================================

  const MILESTONE_STATUS_CONFIG = getMilestoneStatusConfig(t);
  const MILESTONE_TYPE_LABELS = getMilestoneTypeLabels(t);

  const renderStatusBadge = (status: MilestoneStatus) => {
    const config = MILESTONE_STATUS_CONFIG[status];
    return (
      <Badge
        variant="outline"
        className={`
          ${config.color === 'gray' ? 'border-gray-300 text-gray-600 bg-gray-50' : ''}
          ${config.color === 'blue' ? 'border-blue-300 text-blue-700 bg-blue-50' : ''}
          ${config.color === 'green' ? 'border-green-300 text-green-700 bg-green-50' : ''}
          ${config.color === 'amber' ? 'border-amber-300 text-amber-700 bg-amber-50' : ''}
          ${config.color === 'red' ? 'border-red-300 text-red-700 bg-red-50' : ''}
        `}
      >
        {config.icon}
        <span className="ml-1">{config.label}</span>
      </Badge>
    );
  };

  const renderCompactView = () => (
    <div className="space-y-2">
      <div className="flex justify-between items-center text-sm">
        <span className="text-gray-500">{t('serviceReferrals.milestones.progress', 'Progress')}</span>
        <span className="font-medium">{achievedCount}/{milestones.length}</span>
      </div>
      <Progress value={progressPct} className="h-2" />
      {earnedIncentive > 0 && (
        <div className="flex items-center gap-1 text-sm text-green-600">
          <DollarSign className="w-3 h-3" />
          {formatCurrency(earnedIncentive)} {t('serviceReferrals.milestones.earned', 'earned')}
        </div>
      )}
      {overdueCount > 0 && (
        <div className="flex items-center gap-1 text-sm text-amber-600">
          <AlertTriangle className="w-3 h-3" />
          {overdueCount} {t('serviceReferrals.milestones.overdue', 'overdue')}
        </div>
      )}
    </div>
  );

  // ========================================================================
  // RENDER
  // ========================================================================

  if (compact) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Award className="w-4 h-4" />
            {t('serviceReferrals.milestones.title', 'Milestones')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : milestones.length === 0 ? (
            <p className="text-xs text-gray-500">{t('serviceReferrals.milestones.noMilestones', 'No milestones defined')}</p>
          ) : (
            renderCompactView()
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Award className="w-5 h-5" />
              {t('serviceReferrals.milestones.tracking', 'Milestone Tracking')}
            </CardTitle>
            <CardDescription>
              {t('serviceReferrals.milestones.description', 'Track service milestones and incentives')}
            </CardDescription>
          </div>
          {achievedCount === milestones.length && milestones.length > 0 && (
            <Badge className="bg-green-500">
              <Trophy className="w-4 h-4 mr-1" />
              {t('serviceReferrals.milestones.allComplete', 'All Complete!')}
            </Badge>
          )}
        </div>

        {/* Summary Stats */}
        {milestones.length > 0 && (
          <div className="mt-4 grid grid-cols-4 gap-3">
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-xl font-bold">{achievedCount}/{milestones.length}</div>
              <div className="text-xs text-gray-500">{t('serviceReferrals.milestones.achieved', 'Achieved')}</div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <div className="text-xl font-bold text-blue-700">{requiredAchieved}/{requiredCount}</div>
              <div className="text-xs text-blue-600">{t('serviceReferrals.milestones.required', 'Required')}</div>
            </div>
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <div className="text-xl font-bold text-green-700">{formatCurrency(earnedIncentive)}</div>
              <div className="text-xs text-green-600">{t('serviceReferrals.milestones.earnedLabel', 'Earned')}</div>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg text-center">
              <div className="text-xl font-bold text-purple-700">{formatCurrency(totalIncentive)}</div>
              <div className="text-xs text-purple-600">{t('serviceReferrals.milestones.totalPossible', 'Total Possible')}</div>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        {milestones.length > 0 && (
          <div className="mt-4 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('serviceReferrals.milestones.overallProgress', 'Overall Progress')}</span>
              <span className="font-medium">{progressPct}%</span>
            </div>
            <Progress value={progressPct} className="h-2" />
          </div>
        )}
      </CardHeader>

      <CardContent>
        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="py-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        )}

        {/* Empty State */}
        {!loading && milestones.length === 0 && (
          <div className="py-8 text-center text-gray-500">
            <Award className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>{t('serviceReferrals.milestones.noMilestonesForType', 'No milestones defined for this service type')}</p>
          </div>
        )}

        {/* Milestones Timeline */}
        {!loading && milestones.length > 0 && (
          <div className="space-y-4">
            {milestones.map((milestone, index) => {
              const isLast = index === milestones.length - 1;
              const canAchieve = !readOnly && milestone.status !== 'achieved' && milestone.status !== 'skipped';

              return (
                <div key={milestone.id} className="relative">
                  {/* Timeline Line */}
                  {!isLast && (
                    <div
                      className={`absolute left-4 top-8 w-0.5 h-full -ml-px ${
                        milestone.status === 'achieved' ? 'bg-green-300' : 'bg-gray-200'
                      }`}
                    />
                  )}

                  <div className="flex gap-4">
                    {/* Timeline Dot */}
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                        milestone.status === 'achieved'
                          ? 'bg-green-500 text-white'
                          : milestone.is_overdue
                          ? 'bg-amber-500 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {milestone.status === 'achieved' ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : milestone.is_overdue ? (
                        <AlertTriangle className="w-5 h-5" />
                      ) : (
                        <span className="text-sm font-medium">{milestone.sequence_number}</span>
                      )}
                    </div>

                    {/* Milestone Content */}
                    <div className="flex-1 pb-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{milestone.milestone_name}</h4>
                            {milestone.is_required && (
                              <Badge variant="outline" className="text-xs">{t('serviceReferrals.milestones.required', 'Required')}</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            {renderStatusBadge(milestone.status)}
                            <Badge variant="outline" className="text-xs">
                              {MILESTONE_TYPE_LABELS[milestone.milestone_type]}
                            </Badge>
                            {milestone.milestone_code && (
                              <span className="text-xs font-mono">{milestone.milestone_code}</span>
                            )}
                          </div>
                        </div>

                        {/* Incentive Amount */}
                        {milestone.incentive_amount && milestone.incentive_amount > 0 && (
                          <div className="text-right">
                            {milestone.incentive_triggered ? (
                              <div className="text-green-600 font-medium">
                                {formatCurrency(milestone.incentive_amount_earned)}
                                <div className="text-xs text-green-500">{t('serviceReferrals.milestones.earnedLabel', 'Earned')}</div>
                              </div>
                            ) : (
                              <div className="text-gray-500">
                                {formatCurrency(milestone.incentive_amount)}
                                <div className="text-xs">{t('serviceReferrals.milestones.possible', 'Possible')}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Target Date / Achievement Date */}
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        {milestone.target_date && (
                          <div className="flex items-center gap-1 text-gray-500">
                            <Calendar className="w-3 h-3" />
                            {t('serviceReferrals.milestones.target', 'Target')}: {formatDate(milestone.target_date)}
                          </div>
                        )}
                        {milestone.achieved_at && (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-3 h-3" />
                            {t('serviceReferrals.milestones.achievedLabel', 'Achieved')}: {formatDate(milestone.achieved_at)}
                          </div>
                        )}
                        {milestone.is_overdue && !milestone.achieved_at && (
                          <div className="flex items-center gap-1 text-amber-600">
                            <AlertTriangle className="w-3 h-3" />
                            {t('serviceReferrals.milestones.overdueLabel', 'Overdue')}
                          </div>
                        )}
                      </div>

                      {/* Progress Notes */}
                      {milestone.progress_notes && (
                        <p className="mt-2 text-sm text-gray-500 italic">{milestone.progress_notes}</p>
                      )}

                      {/* Achievement Notes */}
                      {milestone.achievement_notes && (
                        <p className="mt-2 text-sm text-green-600">{milestone.achievement_notes}</p>
                      )}

                      {/* Action Button */}
                      {canAchieve && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3"
                          onClick={() => setShowAchieveModal(milestone.milestone_definition_id)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          {t('serviceReferrals.milestones.markAsAchieved', 'Mark as Achieved')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Achievement Modal */}
        {showAchieveModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle className="text-lg">{t('serviceReferrals.milestones.markAsAchievedTitle', 'Mark Milestone as Achieved')}</CardTitle>
                <CardDescription>
                  {milestones.find((m) => m.milestone_definition_id === showAchieveModal)?.milestone_name}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="achievement_notes">{t('serviceReferrals.milestones.achievementNotesLabel', 'Achievement Notes (optional)')}</Label>
                  <Textarea
                    id="achievement_notes"
                    value={achievementNotes}
                    onChange={(e) => setAchievementNotes(e.target.value)}
                    placeholder={t('serviceReferrals.milestones.achievementNotesPlaceholder', 'Add notes about how this milestone was achieved...')}
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAchieveModal(null);
                      setAchievementNotes('');
                    }}
                  >
                    {t('serviceReferrals.common.cancel', 'Cancel')}
                  </Button>
                  <Button
                    onClick={() => handleAchieveMilestone(showAchieveModal)}
                    disabled={achieving !== null}
                  >
                    {achieving !== null && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    <CheckCircle className="w-4 h-4 mr-1" />
                    {t('serviceReferrals.milestones.confirmAchievement', 'Confirm Achievement')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default MilestonesDashboard;
